// Global Commitment modal provider — FE-043c.
//
// Owns the open / close / stage state for the modal-first
// Commitment create-and-view system. Any surface (Tracker,
// Account, PlanYear, Touchpoint action item, Playbook apply,
// BeneAI suggestion, Dashboard drill-through) can call into the
// provider via ``useCommitmentModal`` to either pop a quick-create
// modal or jump straight to the full form / view.
//
// Stages:
//   "closed"      → nothing rendered.
//   "quick-create"→ title-only modal; submitting transitions to
//                   "full-create" with the title preserved.
//   "full-create" → full Commitment form modal.
//   "view"        → reserved for the view-modal follow-up. The
//                   provider supports the route today; the actual
//                   modal body is deferred to the next chunk
//                   (see __FOLLOW_UP__ note below).
//
// The provider owns *only* the stage + defaults. Submitting the
// form, mutations, and cache invalidation live in the modal
// components themselves so the provider stays a thin coordination
// surface.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";


export type CommitmentModalStage =
  | "closed"
  | "quick-create"
  | "full-create"
  | "view";


/** Per-call defaults seeded into the create modal. Every key is
 * optional; the modal falls back to its blank form values when
 * a key is missing. */
export interface CommitmentModalDefaults {
  title?: string;
  account_id?: string;
  plan_year_id?: string;
  status?: string;
  category_slug?: string;
  due_date_time?: string;
  source_touchpoint_id?: string | null;
  source_playbook_application_id?: string | null;
}


export interface CommitmentModalReturnContext {
  /** ``location.pathname + location.search`` of the surface that
   * launched the modal. Used by the modal so closing returns
   * the user without disturbing filters / search / view mode. */
  fromUrl?: string;
  /** Optional human label paired with ``fromUrl`` for breadcrumb
   * surfaces like the existing detail page. */
  fromLabel?: string;
}


export interface OpenCreateOptions {
  /** Skip the title-only quick-create stage and go directly to the
   * full form. Set when the caller already has a meaningful title
   * (Touchpoint action item, BeneAI suggestion). */
  skipQuickCreate?: boolean;
  defaults?: CommitmentModalDefaults;
  returnContext?: CommitmentModalReturnContext;
}


export interface OpenViewOptions {
  returnContext?: CommitmentModalReturnContext;
}


export interface CommitmentModalState {
  stage: CommitmentModalStage;
  defaults: CommitmentModalDefaults;
  /** Used while ``stage === "view"``. Empty otherwise. */
  commitmentId: string;
  returnContext: CommitmentModalReturnContext;
}


export interface CommitmentModalApi {
  state: CommitmentModalState;
  openCreateCommitmentModal: (options?: OpenCreateOptions) => void;
  openCommitmentModal: (
    commitmentId: string, options?: OpenViewOptions,
  ) => void;
  /** Promote the quick-create stage into the full-form stage,
   * keeping the in-flight title and any defaults the caller
   * supplied. Called by the quick-create modal once the user
   * confirms a non-empty title. */
  promoteToFullCreate: (titleOverride?: string) => void;
  closeCommitmentModal: () => void;
}


const INITIAL_STATE: CommitmentModalState = {
  stage: "closed",
  defaults: {},
  commitmentId: "",
  returnContext: {},
};


const CommitmentModalContext = createContext<CommitmentModalApi | null>(null);


export function CommitmentModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CommitmentModalState>(INITIAL_STATE);
  // Keep the latest state addressable by ``promoteToFullCreate``
  // without making the callback dependency-chase the latest state
  // closure on every render.
  const stateRef = useRef(state);
  stateRef.current = state;

  const openCreateCommitmentModal = useCallback(
    (options?: OpenCreateOptions) => {
      const defaults = options?.defaults ?? {};
      // Default flow now goes straight to the full form. The
      // ``skipQuickCreate`` option is retained for back-compat with
      // any caller that explicitly opted in, but the legacy
      // title-only stage is no longer reachable from the default
      // path — saves a click and avoids a remount when the user
      // wanted the rest of the fields anyway.
      setState({
        stage: "full-create",
        defaults,
        commitmentId: "",
        returnContext: options?.returnContext ?? {},
      });
    },
    [],
  );

  const openCommitmentModal = useCallback(
    (commitmentId: string, options?: OpenViewOptions) => {
      setState({
        stage: "view",
        defaults: {},
        commitmentId,
        returnContext: options?.returnContext ?? {},
      });
    },
    [],
  );

  const promoteToFullCreate = useCallback(
    (titleOverride?: string) => {
      const prev = stateRef.current;
      const nextDefaults: CommitmentModalDefaults = {
        ...prev.defaults,
        ...(titleOverride !== undefined ? { title: titleOverride } : {}),
      };
      setState({
        stage: "full-create",
        defaults: nextDefaults,
        commitmentId: "",
        returnContext: prev.returnContext,
      });
    },
    [],
  );

  const closeCommitmentModal = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const api = useMemo<CommitmentModalApi>(
    () => ({
      state,
      openCreateCommitmentModal,
      openCommitmentModal,
      promoteToFullCreate,
      closeCommitmentModal,
    }),
    [
      state,
      openCreateCommitmentModal,
      openCommitmentModal,
      promoteToFullCreate,
      closeCommitmentModal,
    ],
  );

  return (
    <CommitmentModalContext.Provider value={api}>
      {children}
    </CommitmentModalContext.Provider>
  );
}


/** Returns the modal API. Throws if called outside the provider —
 * the modal entry-point components live under the dashboard
 * routes and must be wrapped by ``CommitmentModalProvider``. */
export function useCommitmentModal(): CommitmentModalApi {
  const ctx = useContext(CommitmentModalContext);
  if (!ctx) {
    throw new Error(
      "useCommitmentModal must be used within a CommitmentModalProvider",
    );
  }
  return ctx;
}


/** Test-only helper that returns ``null`` when the provider is
 * absent. Pages that *optionally* surface the modal (e.g. a row
 * link that gracefully falls back to the full-page detail when
 * the provider is missing) can call this in place of
 * ``useCommitmentModal``. */
export function useOptionalCommitmentModal(): CommitmentModalApi | null {
  return useContext(CommitmentModalContext);
}
